import React, { useState, useMemo } from 'react';
import { useLens } from './LensProvider';

const QUESTIONS = [
  {
    id: 'body',
    text: 'How does your body feel right now?',
    options: [
      { label: 'Heavy, tired, or tight', B: 0.25, S: 0.35 },
      { label: 'Fine — nothing notable', B: 0.55, S: 0.5 },
      { label: 'Rested, warm, at ease', B: 0.8, S: 0.65 },
    ],
  },
  {
    id: 'mind',
    text: 'If you close your eyes for a moment, what happens?',
    options: [
      { label: 'Racing thoughts, hard to settle', N: 0.8, I_control: 0.7, I_fear: 0.5 },
      { label: 'Busy but manageable', N: 0.5, I_control: 0.4, I_fear: 0.3 },
      { label: 'Quiet — things slow down easily', N: 0.2, I_control: 0.15, I_fear: 0.1 },
    ],
  },
  {
    id: 'heart',
    text: 'How open does the world feel to you today?',
    options: [
      { label: 'Closed off — I want to protect myself', I_openness: 0.15, I_care: 0.3, I_fear: 0.7 },
      { label: 'Guarded but curious', I_openness: 0.5, I_care: 0.5, I_fear: 0.35 },
      { label: 'Open — tender, available, receptive', I_openness: 0.85, I_care: 0.8, I_fear: 0.1 },
    ],
  },
  {
    id: 'meaning',
    text: 'When something unexpected happens, your first response is usually...',
    options: [
      { label: 'I know what this means (and it\'s usually bad)', N: 0.85, I_openness: 0.2 },
      { label: 'I try to figure it out', N: 0.5, I_openness: 0.5 },
      { label: 'I wait and see — it could be anything', N: 0.15, I_openness: 0.8 },
    ],
  },
  {
    id: 'intensity',
    text: 'Right now, how much feels like "too much"?',
    options: [
      { label: 'Almost everything', B: 0.2, S: 0.8, I_fear: 0.7 },
      { label: 'Some things, but I can handle it', B: 0.55, S: 0.55, I_fear: 0.3 },
      { label: 'Very little — I have room', B: 0.75, S: 0.45, I_fear: 0.1 },
    ],
  },
];

const computeLensFromAnswers = (answers) => {
  // Accumulate all parameter nudges, then average
  const accum = { B: [], S: [], N: [], I_openness: [], I_fear: [], I_care: [], I_control: [] };

  answers.forEach((answer) => {
    Object.entries(answer).forEach(([key, value]) => {
      if (key !== 'label' && accum[key]) {
        accum[key].push(value);
      }
    });
  });

  const avg = (arr, fallback) => arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : fallback;

  return {
    B: avg(accum.B, 0.5),
    S: avg(accum.S, 0.5),
    N: avg(accum.N, 0.4),
    I: {
      openness: avg(accum.I_openness, 0.5),
      fear: avg(accum.I_fear, 0.3),
      care: avg(accum.I_care, 0.5),
      control: avg(accum.I_control, 0.3),
    },
  };
};

const generateReading = (pressure, capacityVector, phenomenology) => {
  if (!capacityVector || capacityVector.length === 0) return '';

  const sorted = [...capacityVector].sort((a, b) => a.value - b.value);
  const weakest = sorted[0];
  const strongest = sorted[sorted.length - 1];

  const bodyLine = (() => {
    const bodyAxis = capacityVector.find(a => a.id === 'body');
    if (!bodyAxis) return '';
    if (bodyAxis.value > 0.65) return 'Your body is holding you well right now.';
    if (bodyAxis.value > 0.4) return 'Your body is managing, but there\'s some strain.';
    return 'Your body is carrying a lot right now. That\'s not weakness — it\'s load.';
  })();

  const narrativeLine = (() => {
    const meaningAxis = capacityVector.find(a => a.id === 'meaning');
    if (!meaningAxis) return '';
    if (meaningAxis.value > 0.65) return 'Your interpretations are flexible — you\'re not locked into one story about what things mean.';
    if (meaningAxis.value > 0.4) return 'Your interpretations are somewhat fixed. Some things feel decided before they arrive.';
    return 'Your narrative is quite rigid right now. Things may feel predetermined or inevitable. That\'s the filter talking, not the truth.';
  })();

  const pressureLine = (() => {
    if (pressure < 0.2) return 'The aperture is wide. What\'s flowing through arrives without much compression. This is clarity.';
    if (pressure < 0.5) return 'There\'s some charge. The aperture is narrowed enough that things feel vivid, significant, textured. Not uncomfortable — alive.';
    if (pressure < 0.8) return 'The aperture is strained. More is trying to come through than currently fits. This is overwhelm — not danger, just load.';
    return 'The aperture is very narrow. Almost everything feels like too much. This is pressure at its most intense — and it will shift.';
  })();

  const closingLine = `Your strongest dimension is ${strongest.label.toLowerCase()}. Your most constricted is ${weakest.label.toLowerCase()}. This is not a score — it is a shape. And shapes change.`;

  return { bodyLine, narrativeLine, pressureLine, closingLine };
};

const ApertureMirror = ({ onComplete }) => {
  const { setB, setS, setN, setI } = useLens();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [revealed, setRevealed] = useState(false);
  const [computedLens, setComputedLens] = useState(null);

  const handleAnswer = (option) => {
    const newAnswers = [...answers, option];
    setAnswers(newAnswers);

    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      // Compute and apply lens
      const lens = computeLensFromAnswers(newAnswers);
      setComputedLens(lens);
      setB(lens.B);
      setS(lens.S);
      setN(lens.N);
      setI(lens.I);

      // Small delay for the polygon to compute, then reveal
      setTimeout(() => setRevealed(true), 300);
    }
  };

  if (revealed) {
    return <MirrorReveal computedLens={computedLens} onComplete={onComplete} />;
  }

  const question = QUESTIONS[step];
  const progress = ((step) / QUESTIONS.length) * 100;

  return (
    <div className="mirror-overlay">
      <div className="mirror-content">
        <div className="mirror-progress">
          <div className="mirror-progress-bar" style={{ width: `${progress}%` }} />
        </div>
        <p className="mirror-question">{question.text}</p>
        <div className="mirror-options">
          {question.options.map((option, i) => (
            <button
              key={i}
              className="mirror-option"
              onClick={() => handleAnswer(option)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <p className="mirror-hint">There are no right answers. Go with your gut.</p>
      </div>
    </div>
  );
};

const MirrorReveal = ({ computedLens, onComplete }) => {
  const { pressure, capacityVector, phenomenology } = useLens();

  const reading = useMemo(() => {
    return generateReading(pressure, capacityVector, phenomenology);
  }, [pressure, capacityVector, phenomenology]);

  return (
    <div className="mirror-overlay">
      <div className="mirror-reveal">
        <p className="mirror-reveal-headline">
          {phenomenology?.headline || 'Your Aperture'}
        </p>
        <p className="mirror-reveal-subtitle">
          This is the shape of your opening right now.
        </p>

        <div className="mirror-reading">
          <p>{reading.bodyLine}</p>
          <p>{reading.narrativeLine}</p>
          <p>{reading.pressureLine}</p>
          <p className="mirror-closing">{reading.closingLine}</p>
        </div>

        <p className="mirror-disclaimer">
          This is not a diagnosis. It is a mirror — a way to see why today feels the way it does.
          The aperture moves. Tomorrow's shape will be different.
        </p>

        <button className="landing-button" onClick={onComplete}>
          Explore the Full Model
        </button>
      </div>
    </div>
  );
};

export default ApertureMirror;
